import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Badge,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Icon,
  Text,
  Box,
  useColorMode,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  Button,
  VStack,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
} from '@chakra-ui/react';
import {
  AiOutlineLogout,
  AiOutlineSun,
  AiOutlineMoon,
  AiOutlineBell,
  AiOutlineAlert,
  AiOutlineInfoCircle,
  AiOutlineWarning,
  AiOutlineExclamationCircle,
  AiOutlineCheckCircle,
  AiOutlineLineChart,
  AiOutlineClockCircle,
  AiOutlineCalendar,
  AiOutlineArrowRight,
} from 'react-icons/ai';
import { useAuth } from '../../hooks/auth';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

// ⚠️ Cobranças "Em breve" (pedido do Lucas em 16/07/2026): o motor/sininho ainda
// não está sendo usado da forma correta. Enquanto true: sem poll de notificações,
// sem toasts e sem modal bloqueador — o sino exibe apenas o aviso de "Em breve".
// Para reativar todo o sistema de cobranças, basta voltar esta flag para false.
const COBRANCAS_EM_BREVE = true;

// Severidade: cor + rótulo + ícone (1=Info, 2=Atenção, 3=Crítico).
// Atenção usa o bronze do tema (token `warning` light, #B7791F) — o amarelo
// #ECC94B ficou chamativo demais no contexto de cobranças.
const SEV_META = {
  1: { label: 'Info', color: '#01E975', icon: AiOutlineInfoCircle },
  2: { label: 'Atenção', color: '#B7791F', icon: AiOutlineWarning },
  3: { label: 'Crítico', color: '#FC8181', icon: AiOutlineExclamationCircle },
};

// Metadados por tela validada (rótulo amigável + ícone + para onde levar ao clicar)
const TELA_META = {
  pipeline: { label: 'Pipeline', icon: AiOutlineLineChart, rota: '/comercial/pipeline' },
  atividades: { label: 'Atividades', icon: AiOutlineClockCircle, rota: '/comercial/atividades' },
  calendario: { label: 'Calendário', icon: AiOutlineCalendar, rota: '/calendario' },
};

// Severidade-base por tela quando não há regra habilitada que defina o nível.
const SEV_FALLBACK = { pipeline: 2, atividades: 3, calendario: 2 };

// Quebra a mensagem (linhas separadas por \n no backend) em bullets limpos.
const toBullets = (msg) => (msg || '')
  .split('\n')
  .map((s) => s.trim())
  .filter(Boolean);

// Severidade da tela = maior severidade entre as regras habilitadas daquela tela.
const computeTelaSeverity = (telaId, regras) => {
  const sevs = Object.values(regras || {})
    .filter((r) => r.tela === telaId && r.enabled)
    .map((r) => Number(r.severity) || 1);
  return sevs.length ? Math.max(...sevs) : (SEV_FALLBACK[telaId] || 2);
};

const formatTimeAgo = (dateStr) => {
  const diffMs = new Date() - new Date(dateStr);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  if (diffMins < 1) return 'Agora';
  if (diffMins < 60) return `há ${diffMins} min`;
  if (diffHours < 24) return `há ${diffHours}h`;
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

const Header = () => {
  const { signOut, user } = useAuth();
  const { colorMode, toggleColorMode } = useColorMode();
  const navigate = useNavigate();
  const toast = useToast();

  const displayName = user?.name || user?.email || "Usuário";
  const userEmail = user?.email || "";
  const userInitial = displayName.charAt(0).toUpperCase();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [modalDismissedAt, setModalDismissedAt] = useState(null);

  // Controle de "barulho": ids já anunciados via toast (evita repetir o mesmo alerta)
  const announcedIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);
  const loginSummaryToastShownRef = useRef(false);

  const fetchNotificationsAndConfig = useCallback(async (currentDismissedAt = modalDismissedAt) => {
    try {
      // Busca notificações + config em paralelo (a config define a severidade por tela)
      const [notifRes, configRes] = await Promise.all([
        api.get('/comercial/cobrancas/notificacoes'),
        api.get('/comercial/cobrancas/config'),
      ]);
      const cfg = configRes.data || { pausarTudo: false, toastCriticas: true, regras: {} };
      const dbRow = notifRes.data && notifRes.data.length > 0 ? notifRes.data[0] : null;
      let notifs = [];

      if (dbRow) {
          const isConfirmed = dbRow.user_confirmed === true || dbRow.user_confirmed === 1;
          // updated_at = última varredura do motor (created_at é fixo na criação da linha do usuário)
          const validatedAt = dbRow.updated_at || dbRow.created_at || new Date().toISOString();
          const telas = [
            { id: 'pipeline', valid: dbRow.valid_pipeline, message: dbRow.message_pipeline, sev: dbRow.severity_pipeline },
            { id: 'atividades', valid: dbRow.valid_atividades, message: dbRow.message_atividades, sev: dbRow.severity_atividades },
            { id: 'calendario', valid: dbRow.valid_calendario, message: dbRow.message_calendario, sev: dbRow.severity_calendario },
            // OBS: a tela "Relatórios" está reservada (motor não popula por enquanto).
          ];
          telas.forEach((t) => {
            if (t.valid === false || t.valid === 0) {
              // Severidade real do que disparou (vinda do banco); fallback p/ a
              // heurística em linhas antigas sem a coluna severity_*.
              const severity = Number(t.sev) > 0 ? Number(t.sev) : computeTelaSeverity(t.id, cfg.regras);
              // Cada validação (linha da mensagem do motor) vira uma NOTIFICAÇÃO
              // INDIVIDUAL, carimbada com a tela a que pertence. O id usa o texto
              // para o toast anunciar só o que for realmente novo.
              toBullets(t.message).forEach((texto) => {
                notifs.push({
                  id: `${t.id}::${texto}`,
                  tela: t.id,
                  title: TELA_META[t.id]?.label || t.id,
                  text: texto,
                  severity,
                  read: isConfirmed,
                  validated_at: validatedAt,
                });
              });
            }
          });
      }

      const isPaused = cfg.pausarTudo === true;

      if (isPaused) {
        setNotifications([]);
        setUnreadCount(0);
        setShowBillingModal(false);
      } else {
        setNotifications(notifs);
        const unreads = notifs.filter(n => !n.read);
        setUnreadCount(unreads.length);

        // ── Toasts de cobrança (faz "barulho" a cada alerta novo) ──
        if (firstLoadRef.current) {
          // Primeira carga da sessão: resumo único das pendências não lidas
          firstLoadRef.current = false;
          unreads.forEach(n => announcedIdsRef.current.add(n.id));
          if (unreads.length > 0 && !loginSummaryToastShownRef.current && !toast.isActive('cobranca-resumo-login')) {
            loginSummaryToastShownRef.current = true;
            toast({
              id: 'cobranca-resumo-login',
              title: `Você tem ${unreads.length} pendência(s) comercial(is)`,
              description: 'Abra o sino de notificações para revisar.',
              status: unreads.some(n => n.severity === 3) ? 'error' : 'warning',
              duration: 8000,
              isClosable: true,
              position: 'top-right',
            });
          }
        } else {
          // Polls seguintes: anuncia individualmente cada alerta novo não lido
          unreads.forEach(n => {
            if (!announcedIdsRef.current.has(n.id)) {
              announcedIdsRef.current.add(n.id);
              toast({
                title: `${n.title}: ${SEV_META[n.severity]?.label || ''}`,
                description: n.text,
                status: n.severity === 3 ? 'error' : n.severity === 2 ? 'warning' : 'info',
                duration: 7000,
                isClosable: true,
                position: 'top-right',
              });
            }
          });
        }

        // Verifica se há alguma não lida crítica (severidade 3)
        const unreadCriticals = unreads.filter(n => n.severity === 3);

        // O modal é derivado de user_confirmed: enquanto houver crítico não
        // confirmado, ele aparece — a menos que o usuário tenha clicado
        // "Revisar mais tarde" nesta sessão (currentDismissedAt). Ao confirmar
        // (Marcar lidas), user_confirmed=1 zera os não lidos e o modal some.
        if (unreadCriticals.length > 0 && cfg.toastCriticas !== false) {
          if (!currentDismissedAt) {
            setShowBillingModal(true);
          }
        } else {
          setShowBillingModal(false);
        }
      }
    } catch (err) {
      console.error("Erro ao carregar dados de cobrança no header:", err);
    }
  }, [modalDismissedAt, toast]);

  useEffect(() => {
    if (COBRANCAS_EM_BREVE) return; // cobranças pausadas — sino só exibe o aviso
    if (user) {
      fetchNotificationsAndConfig(modalDismissedAt);
      // Poll a cada 30 segundos (cobrança mais insistente)
      const interval = setInterval(() => {
        fetchNotificationsAndConfig(modalDismissedAt);
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user, modalDismissedAt, fetchNotificationsAndConfig]);

  // Título da aba do navegador também "cobra" o usuário: (N) Nogle Tech
  useEffect(() => {
    const base = 'Nogle Tech';
    document.title = unreadCount > 0 ? `(${unreadCount}) ${base}` : base;
    return () => { document.title = base; };
  }, [unreadCount]);

  const handleMarkAllRead = async () => {
    try {
      await api.post('/comercial/cobrancas/notificacoes/ler');
      setShowBillingModal(false);
      fetchNotificationsAndConfig(modalDismissedAt);
    } catch (err) {
      console.error("Erro ao marcar notificações como lidas:", err);
    }
  };

  const handleDismissModal = () => {
    // "Revisar mais tarde": suprime o modal nesta sessão (o sininho continua
    // mostrando as pendências; ao confirmar tudo, user_confirmed=1 no banco).
    setModalDismissedAt(Date.now());
    setShowBillingModal(false);
  };

  const iconBtnStyle = {
    variant: 'ghost',
    fontSize: '20px',
    color: 'textSecondary',
    borderRadius: 'lg',
    _hover: { color: 'secondary', bg: 'surfaceHover' },
  };

  const hasCritical = unreadCount > 0 && notifications.some(n => !n.read && n.severity === 3);
  const unreadCriticalNotifications = notifications.filter(n => !n.read && n.severity === 3);

  return (
    <Flex direction="column" w="100%">
      {/* Estilos CSS Embutidos para Animações Visuais Chamativas */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes billing-periodic-shake {
          0%, 70%, 100% { transform: rotate(0); }
          72% { transform: rotate(15deg); }
          74% { transform: rotate(-15deg); }
          76% { transform: rotate(12deg); }
          78% { transform: rotate(-12deg); }
          80% { transform: rotate(10deg); }
          82% { transform: rotate(-10deg); }
          84% { transform: rotate(0); }
        }

        @keyframes billing-pulse-dot {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(252, 129, 129, 0.8); }
          70% { transform: scale(1.15); box-shadow: 0 0 0 10px rgba(252, 129, 129, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(252, 129, 129, 0); }
        }

        @keyframes billing-glow-critical {
          0%, 100% {
            border-color: rgba(252, 129, 129, 0.4);
            background-color: rgba(252, 129, 129, 0.05);
            box-shadow: 0 0 4px rgba(252, 129, 129, 0.2);
          }
          50% {
            border-color: rgba(252, 129, 129, 1);
            background-color: rgba(252, 129, 129, 0.25);
            box-shadow: 0 0 16px rgba(252, 129, 129, 0.6);
          }
        }

        @keyframes billing-text-flash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes billing-modal-glow {
          0%, 100% { box-shadow: 0 0 15px rgba(252, 129, 129, 0.2); }
          50% { box-shadow: 0 0 35px rgba(252, 129, 129, 0.6); }
        }

        @keyframes billing-topbar {
          0% { background-position: 0% 0; }
          100% { background-position: 200% 0; }
        }

        @keyframes billing-card-critical {
          0%, 100% { background-color: rgba(252, 129, 129, 0.05); }
          50% { background-color: rgba(252, 129, 129, 0.16); }
        }

        .billing-bell-shake {
          animation: billing-periodic-shake 4s infinite ease-in-out;
          transform-origin: top center;
        }
      `}} />


      {/* 2. Cabeçalho Principal (sem título de página — mantido limpo) */}
      <Flex
        as="header"
        h="20"
        alignItems="center"
        justifyContent="flex-end"
        px={8}
        bg="surface"
        borderBottomWidth="1px"
        borderColor="borderPrimary"
        transition="all 0.2s"
      >
        <Flex align="center" gap={3}>
          {/* Toggle Mode Button (Sun / Moon) */}
          <IconButton
            aria-label="Alternar tema"
            icon={colorMode === 'dark' ? <AiOutlineSun /> : <AiOutlineMoon />}
            onClick={toggleColorMode}
            {...iconBtnStyle}
          />

          {/* Notification Bell with Popover Dropdown */}
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <Box position="relative" display="inline-block">
                <IconButton
                  aria-label="Notificações"
                  icon={<AiOutlineBell className={unreadCount > 0 ? "billing-bell-shake" : ""} />}
                  style={hasCritical ? {
                    animation: 'billing-glow-critical 1.5s infinite',
                    border: '1px solid #FC8181',
                    borderRadius: '8px'
                  } : {}}
                  {...iconBtnStyle}
                />
                {unreadCount > 0 && (
                  <Flex
                    position="absolute"
                    top="2px"
                    right="2px"
                    minW="16px"
                    h="16px"
                    px="4px"
                    bg="danger"
                    color="white"
                    fontSize="9px"
                    fontWeight="900"
                    borderRadius="full"
                    border="2px solid"
                    borderColor="surface"
                    align="center"
                    justify="center"
                    pointerEvents="none"
                    style={{
                      animation: 'billing-pulse-dot 1.5s infinite'
                    }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Flex>
                )}
              </Box>
            </PopoverTrigger>
            <PopoverContent
              bg="surface"
              borderColor={unreadCount > 0 ? 'rgba(1, 233, 117, 0.45)' : 'borderPrimary'}
              color="textPrimary"
              w="400px"
              boxShadow={unreadCount > 0 ? '0 12px 34px rgba(0,0,0,0.30), 0 0 22px rgba(1, 233, 117, 0.18)' : '0 12px 34px rgba(0,0,0,0.30)'}
              borderRadius="xl"
              overflow="hidden"
              _focus={{ outline: 'none', boxShadow: unreadCount > 0 ? '0 12px 34px rgba(0,0,0,0.30), 0 0 22px rgba(1, 233, 117, 0.18)' : '0 12px 34px rgba(0,0,0,0.30)' }}
            >
              <PopoverArrow bg="surface" borderColor="borderPrimary" />
              {/* Barra de urgência animada: visível enquanto houver pendência não lida (verde Nogle) */}
              {unreadCount > 0 && (
                <Box
                  h="3px"
                  w="100%"
                  style={{
                    backgroundImage: 'linear-gradient(90deg, rgba(1,233,117,0.15), #01E975, rgba(1,233,117,0.15))',
                    backgroundSize: '200% 100%',
                    animation: 'billing-topbar 2.2s linear infinite',
                  }}
                />
              )}
              <PopoverHeader
                borderBottom="1px solid"
                borderColor={unreadCount > 0 ? 'rgba(1, 233, 117, 0.30)' : 'borderPrimary'}
                bg={unreadCount > 0 ? 'rgba(1, 233, 117, 0.07)' : 'transparent'}
                px={4}
                py={3}
              >
                <Flex justify="space-between" align="center" gap={2}>
                  {unreadCount > 0 ? (
                    <HStack spacing={2.5} minW={0}>
                      <Flex
                        w="32px"
                        h="32px"
                        borderRadius="lg"
                        bg="rgba(1, 233, 117, 0.14)"
                        color="#01E975"
                        align="center"
                        justify="center"
                        flexShrink={0}
                        style={{ animation: 'billing-text-flash 1.6s infinite' }}
                      >
                        <Icon as={AiOutlineAlert} boxSize="17px" />
                      </Flex>
                      <Box minW={0}>
                        <Text fontWeight="900" fontSize="13px" color="#01E975" textTransform="uppercase" letterSpacing="0.5px" noOfLines={1}>
                          Cobrança Comercial
                        </Text>
                        <Text fontSize="10px" color="textMuted" noOfLines={1}>
                          {unreadCount} pendência(s) aguardando a sua ação
                        </Text>
                      </Box>
                    </HStack>
                  ) : (
                    <Text fontWeight="800" fontSize="sm">Notificações</Text>
                  )}
                  {unreadCount > 0 && (
                    <Button
                      size="xs"
                      variant="link"
                      color="secondary"
                      onClick={handleMarkAllRead}
                      fontSize="10px"
                      fontWeight="800"
                      flexShrink={0}
                      _hover={{ color: 'secondaryHover' }}
                    >
                      Marcar todas lidas
                    </Button>
                  )}
                </Flex>
              </PopoverHeader>
              <PopoverBody maxH="380px" overflowY="auto" p={0}>
                {COBRANCAS_EM_BREVE ? (
                  <VStack spacing={3} py={10} px={6} textAlign="center">
                    <Flex w="46px" h="46px" borderRadius="full" bg="rgba(1,233,117,0.10)" color="secondary" align="center" justify="center">
                      <Icon as={AiOutlineBell} boxSize="22px" />
                    </Flex>
                    <Badge
                      bg="secondarySubtle"
                      color="secondary"
                      fontSize="10px"
                      fontWeight="800"
                      textTransform="uppercase"
                      letterSpacing="1px"
                      px={3}
                      py={1}
                      borderRadius="full"
                    >
                      <HStack spacing={1}>
                        <Icon as={AiOutlineClockCircle} boxSize="11px" />
                        <Text>Em breve</Text>
                      </HStack>
                    </Badge>
                    <Text color="textPrimary" fontSize="sm" fontWeight="800">Notificações em produção</Text>
                    <Text color="textMuted" fontSize="xs" textAlign="center" maxW="280px">
                      As cobranças automáticas do Comercial estão em ajuste e serão reativadas em breve.
                    </Text>
                  </VStack>
                ) : notifications.length === 0 ? (
                  <VStack spacing={2} py={10} px={4}>
                    <Flex w="44px" h="44px" borderRadius="full" bg="rgba(1,233,117,0.10)" color="secondary" align="center" justify="center">
                      <Icon as={AiOutlineCheckCircle} boxSize="22px" />
                    </Flex>
                    <Text color="textPrimary" fontSize="sm" fontWeight="800">Tudo em dia!</Text>
                    <Text color="textMuted" fontSize="xs" textAlign="center">Nenhuma pendência comercial no momento.</Text>
                  </VStack>
                ) : (
                  <VStack spacing={0} align="stretch">
                    {notifications.slice(0, 15).map((n) => {
                      const sev = SEV_META[n.severity] || SEV_META[1];
                      const tela = TELA_META[n.tela] || {};
                      return (
                        <Box
                          key={n.id}
                          px={4}
                          py={3.5}
                          borderBottom="1px solid"
                          borderColor="borderPrimary"
                          borderLeft="4px solid"
                          borderLeftColor={n.read ? 'transparent' : sev.color}
                          bg={!n.read ? `${sev.color}0D` : 'transparent'}
                          style={!n.read && n.severity === 3 ? { animation: 'billing-card-critical 2s infinite ease-in-out' } : {}}
                          _hover={{ bg: 'surfaceHover' }}
                          transition="background 0.15s"
                          cursor={tela.rota ? 'pointer' : 'default'}
                          onClick={() => tela.rota && navigate(tela.rota)}
                        >
                          {/* Cabeçalho do card: tela + chip de severidade */}
                          <Flex align="center" justify="space-between" mb={2} gap={2}>
                            <HStack spacing={2} minW={0}>
                              <Flex w="26px" h="26px" borderRadius="md" bg={`${sev.color}1E`} color={sev.color} align="center" justify="center" flexShrink={0}>
                                <Icon as={tela.icon || sev.icon} boxSize="15px" />
                              </Flex>
                              <Text fontSize="sm" fontWeight="800" color="textPrimary" noOfLines={1}>
                                {n.title}
                              </Text>
                            </HStack>
                            <Flex align="center" gap={1} px={2} py="2px" borderRadius="full" bg={`${sev.color}1A`} border="1px solid" borderColor={`${sev.color}55`} flexShrink={0}>
                              <Box w="6px" h="6px" borderRadius="full" bg={sev.color} />
                              <Text fontSize="9px" fontWeight="800" color={sev.color} textTransform="uppercase" letterSpacing="0.4px">
                                {sev.label}
                              </Text>
                            </Flex>
                          </Flex>

                          {/* Mensagem da notificação (uma validação por card) */}
                          <Text fontSize="12px" color="textSecondary" lineHeight="1.5" pl={1}>
                            {n.text}
                          </Text>

                          {/* Rodapé: status de cobrança + ação direta para resolver */}
                          <Flex align="center" justify="space-between" mt={3} gap={2}>
                            {!n.read ? (
                              <HStack spacing={1.5} minW={0}>
                                <Box w="6px" h="6px" borderRadius="full" bg={sev.color} flexShrink={0} style={{ animation: 'billing-text-flash 1.4s infinite' }} />
                                <Text fontSize="9px" fontWeight="800" color={sev.color} textTransform="uppercase" letterSpacing="0.4px" noOfLines={1}>
                                  Aguardando ação • {formatTimeAgo(n.validated_at)}
                                </Text>
                              </HStack>
                            ) : (
                              <Text fontSize="9px" color="textMuted">{formatTimeAgo(n.validated_at)}</Text>
                            )}
                            {tela.rota && (
                              <Button
                                size="xs"
                                h="24px"
                                px={2.5}
                                bg={!n.read ? sev.color : `${sev.color}1A`}
                                color={!n.read ? 'black' : sev.color}
                                border="1px solid"
                                borderColor={!n.read ? sev.color : `${sev.color}55`}
                                fontSize="10px"
                                fontWeight="900"
                                borderRadius="md"
                                flexShrink={0}
                                rightIcon={<Icon as={AiOutlineArrowRight} boxSize="11px" />}
                                _hover={{ opacity: 0.85, bg: !n.read ? sev.color : `${sev.color}2E` }}
                                onClick={(e) => { e.stopPropagation(); navigate(tela.rota); }}
                              >
                                Resolver agora
                              </Button>
                            )}
                          </Flex>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
              </PopoverBody>
            </PopoverContent>
          </Popover>

          {user && (
            <Menu>
              <MenuButton
                as={Box}
                cursor="pointer"
                w="32px"
                h="32px"
                borderRadius="full"
                bg="secondary"
                display="grid"
                placeItems="center"
                _focus={{
                  boxShadow: '0 0 0 2px var(--chakra-colors-secondary)',
                  border: 'none',
                  outline: 'none'
                }}
              >
                <Text color="primary" fontWeight="bold" fontSize="md" lineHeight="1">
                  {userInitial}
                </Text>
              </MenuButton>

              <MenuList bg="surface" borderColor="borderPrimary" p={1} zIndex={2000}>
                <Box px={3} py={2} mb={2} borderBottom="1px solid" borderColor="borderPrimary">
                  <Text fontWeight="bold" color="textPrimary">{displayName}</Text>
                  <Text fontSize="xs" color="textMuted">{userEmail}</Text>
                </Box>
                <MenuDivider borderColor="borderPrimary" />
                <MenuItem
                  icon={<Icon as={AiOutlineLogout} w={4} h={4} />}
                  onClick={signOut}
                  bg="transparent"
                  color="danger"
                  _hover={{ bg: 'surfaceHover', color: 'danger' }}
                >
                  Sair
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </Flex>
      </Flex>

      {/* 3. Modal Bloqueador Recorrente de Cobrança Administrativa */}
      <Modal
        isOpen={showBillingModal && unreadCriticalNotifications.length > 0}
        onClose={handleDismissModal}
        size="lg"
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay backdropFilter="blur(15px)" bg="rgba(15, 15, 15, 0.85)" />
        <ModalContent
          bg="#1A1D24"
          border="2px solid #FC8181"
          color="white"
          borderRadius="2xl"
          p={1}
          style={{
            animation: 'billing-modal-glow 3s infinite ease-in-out'
          }}
        >
          <ModalHeader borderBottom="1px solid" borderColor="rgba(252, 129, 129, 0.2)" pb={4} pt={5}>
            <HStack spacing={3}>
              <Flex
                w="40px"
                h="40px"
                borderRadius="xl"
                bg="rgba(252, 129, 129, 0.15)"
                color="#FC8181"
                align="center"
                justify="center"
                style={{ animation: 'billing-text-flash 1.2s infinite' }}
              >
                <Icon as={AiOutlineExclamationCircle} boxSize="22px" />
              </Flex>
              <VStack align="start" spacing={0}>
                <Text fontSize="md" fontWeight="950" color="#FC8181" letterSpacing="0.5px">
                  COBRANÇA COMERCIAL: PENDÊNCIAS CRÍTICAS
                </Text>
                <Text fontSize="11px" color="gray.400">
                  Aviso de regularização administrativa obrigatória
                </Text>
              </VStack>
            </HStack>
          </ModalHeader>

          <ModalBody py={6}>
            <VStack spacing={5} align="stretch">
              <Box bg="rgba(252, 129, 129, 0.08)" p={4} borderRadius="xl" border="1px solid" borderColor="rgba(252, 129, 129, 0.25)">
                <Text fontSize="xs" fontWeight="800" color="#FC8181" mb={1} textTransform="uppercase">
                  Por que esta tela apareceu?
                </Text>
                <Text fontSize="xs" color="gray.300" lineHeight="1.6">
                  Seu perfil possui <Text as="span" fontWeight="800" color="white">pendências comerciais críticas não resolvidas</Text>. O atraso de atividades e negócios afeta diretamente o funil de vendas. Esta tela de cobrança continuará aparecendo periodicamente até que as pendências abaixo sejam regularizadas ou lidas.
                </Text>
              </Box>

              <Text fontSize="xs" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.5px">
                Pendências Críticas Ativas ({unreadCriticalNotifications.length}):
              </Text>

              <VStack spacing={3} align="stretch" maxH="220px" overflowY="auto" pr={1}>
                {unreadCriticalNotifications.map(n => (
                  <Flex
                    key={n.id}
                    p={3.5}
                    bg="rgba(255,255,255,0.03)"
                    borderRadius="xl"
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.06)"
                    gap={3}
                    align="start"
                  >
                    <Icon as={AiOutlineWarning} color="#FC8181" boxSize="16px" mt={0.5} />
                    <Box flex="1">
                      <Text fontSize="xs" fontWeight="800" color="white">
                        {n.title}
                      </Text>
                      <Text fontSize="11px" color="gray.400" lineHeight="1.5" mt={1.5} mb={2}>
                        {n.text}
                      </Text>
                      <Text fontSize="9px" color="gray.500">
                        Gerado {formatTimeAgo(n.validated_at)}
                      </Text>
                    </Box>
                  </Flex>
                ))}
              </VStack>
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="rgba(255,255,255,0.06)" gap={3} pb={5} flexWrap="wrap" justifyContent="center" flexDirection={{ base: 'column', sm: 'row' }}>
            <Button
              variant="outline"
              size="sm"
              w={{ base: '100%', sm: 'auto' }}
              borderColor="rgba(255,255,255,0.15)"
              color="gray.400"
              _hover={{ bg: "rgba(255,255,255,0.05)", color: "white" }}
              onClick={handleDismissModal}
            >
              Revisar mais tarde
            </Button>
            <Button
              colorScheme="red"
              bg="#FC8181"
              color="black"
              w={{ base: '100%', sm: 'auto' }}
              _hover={{ bg: "#E57373" }}
              size="sm"
              fontWeight="800"
              onClick={handleMarkAllRead}
            >
              Marcar Lidas
            </Button>
            <Button
              bg="secondary"
              color="primary"
              w={{ base: '100%', sm: 'auto' }}
              _hover={{ bg: "secondaryHover" }}
              size="sm"
              fontWeight="900"
              onClick={() => {
                handleDismissModal();
                navigate('/controladores');
              }}
            >
              Painel de Cobranças
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default Header;
